/**
 * Route between Connect (pre-pair) and Dashboard (paired) states.
 *
 * Initial boot:
 *   - Parse ?shop and ?code from URL (set by Worker /oauth/callback)
 *   - Fetch GET /status/:shop
 *     - If paired -> Dashboard
 *     - If not paired and we have a code -> Connect (waiting for CLI)
 *     - If not paired and no code -> Connect (expired, show reinstall CTA)
 */
import { useEffect, useState } from "react";
import { Box, Page, Spinner, Text } from "@shopify/polaris";
import type { StatusBlob } from "@erpclaw-shopify/shared";
import { Connect } from "./pages/Connect";
import { Dashboard } from "./pages/Dashboard";
import { ApiError, getStatus, pairInit } from "./lib/api";

type BootState =
  | { kind: "loading" }
  | { kind: "missing_shop" }
  | { kind: "connect"; shop: string; code: string | null }
  | { kind: "paired"; shop: string; blob: StatusBlob; lastUpdatedAt: string }
  | { kind: "error"; message: string };

export function App() {
  const [state, setState] = useState<BootState>({ kind: "loading" });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shop = params.get("shop");
    const code = params.get("code");
    if (!shop) {
      setState({ kind: "missing_shop" });
      return;
    }
    (async () => {
      try {
        const resp = await getStatus(shop);
        if (resp.paired) {
          setState({
            kind: "paired",
            shop,
            blob: resp.status,
            lastUpdatedAt: resp.last_updated_at,
          });
          return;
        }
        // Not paired. If we already have a code from the URL, use it.
        // Otherwise mint a fresh one via Token Exchange. The Token
        // Exchange path is the modern Shopify flow for embedded apps
        // and always returns expiring offline tokens.
        if (code) {
          setState({ kind: "connect", shop, code });
          return;
        }
        try {
          const init = await pairInit();
          setState({ kind: "connect", shop, code: init.pairing_code });
        } catch (initErr) {
          // Pair-init failed. Surface the error so Nik can see why
          // (most often: session token expired or Token Exchange
          // returned a non-200). Connect screen with code=null shows
          // the merchant a "missing or expired" message.
          if (initErr instanceof ApiError) {
            setState({
              kind: "error",
              message: `${initErr.code}: ${initErr.message}`,
            });
          } else {
            setState({ kind: "connect", shop, code: null });
          }
        }
      } catch (err) {
        if (err instanceof ApiError) {
          setState({ kind: "error", message: `${err.code}: ${err.message}` });
        } else {
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : "unknown error",
          });
        }
      }
    })();
  }, []);

  const onPaired = () => {
    // Poller in Connect.tsx told us pairing completed. Re-fetch to transition.
    const params = new URLSearchParams(window.location.search);
    const shop = params.get("shop");
    if (!shop) return;
    getStatus(shop)
      .then((resp) => {
        if (resp.paired) {
          setState({
            kind: "paired",
            shop,
            blob: resp.status,
            lastUpdatedAt: resp.last_updated_at,
          });
        }
      })
      .catch(() => {
        // Leave as-is; next poll will retry.
      });
  };

  if (state.kind === "loading") {
    return (
      <Page>
        <Box padding="400">
          <Spinner accessibilityLabel="Loading ERPClaw status" />
        </Box>
      </Page>
    );
  }
  if (state.kind === "missing_shop") {
    return (
      <Page title="ERPClaw">
        <Box padding="400">
          <Text as="p" variant="bodyMd">
            This app must be opened from your Shopify admin.
          </Text>
        </Box>
      </Page>
    );
  }
  if (state.kind === "error") {
    return (
      <Page title="ERPClaw">
        <Box padding="400">
          <Text as="p" variant="bodyMd" tone="critical">
            Could not reach ERPClaw service: {state.message}
          </Text>
        </Box>
      </Page>
    );
  }
  if (state.kind === "connect") {
    return <Connect shop={state.shop} code={state.code} onPaired={onPaired} />;
  }
  return (
    <Dashboard
      shop={state.shop}
      initial={state.blob}
      initialLastUpdatedAt={state.lastUpdatedAt}
    />
  );
}
